import axiosInstance from '../services/AxiosInstance';

/**
 * Shared Subscription Cache Utility
 * Centralized cache for subscription data to prevent multiple API calls
 */

// Shared cache for subscription data
let subscriptionDataCache = null;
let cacheTimestamp = null;
let pendingRequest = null; // Track pending request to prevent duplicate calls
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch user's active subscriptions from API
 * @returns {Promise<Object|null>} - Raw subscription response or null
 */
const fetchSubscriptionsFromAPI = async () => {
  try {
    const response = await axiosInstance.get('/userSubscription/subscriptions?status=active');
    return response.data;
  } catch (error) {
    console.error('Error fetching subscriptions:', error);
    return null;
  }
};

/**
 * Get user's active subscriptions with shared caching
 * This function ensures only one API call is made even if multiple components request it simultaneously
 * @param {boolean} forceRefresh - Force refresh cache even if valid
 * @returns {Promise<Object|null>} - Subscription data with structure: { subscriptions: [...], count: number }
 */
export const getActiveSubscriptions = async (forceRefresh = false) => {
  const now = Date.now();
  
  // Return cached data if it's still valid and not forcing refresh
  if (!forceRefresh && subscriptionDataCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
    return subscriptionDataCache;
  }
  
  // If there's already a pending request, wait for it instead of making a new one
  if (pendingRequest) {
    try {
      return await pendingRequest;
    } catch (error) {
      // If pending request fails, continue to make a new request
      pendingRequest = null;
    }
  }
  
  // Create a new request and store it as pending
  pendingRequest = fetchSubscriptionsFromAPI();
  
  try {
    const data = await pendingRequest;
    
    // Only cache if we got valid data
    if (data && data.subscriptions) {
      subscriptionDataCache = data;
      cacheTimestamp = now;
    }
    
    pendingRequest = null;
    return data;
  } catch (error) {
    pendingRequest = null;
    throw error;
  }
};

/**
 * Clear the subscription cache
 * Useful when subscription changes (e.g., after subscribing/unsubscribing)
 */
export const clearSubscriptionCache = () => {
  subscriptionDataCache = null;
  cacheTimestamp = null;
  pendingRequest = null;
};

/**
 * Get cache info for debugging
 * @returns {Object} - Cache status information
 */
export const getCacheInfo = () => {
  return {
    hasCache: subscriptionDataCache !== null,
    cacheAge: cacheTimestamp ? Date.now() - cacheTimestamp : null,
    cacheValid: cacheTimestamp && (Date.now() - cacheTimestamp) < CACHE_DURATION,
    hasPendingRequest: pendingRequest !== null
  };
};
